/**
 * The two things a screen renders to explain itself.
 *
 * Both return `null` when Help mode is off, so a screen carries its
 * help inline and pays nothing for it the rest of the time. Neither
 * ever portals, positions, overlays or steals focus: they are ordinary
 * blocks in the layout that is already there.
 */
import { useHelpMode } from './useHelpMode';
import { severityOf } from './help-mode';
import { useTranslate } from '../i18n/use-translate';
import { hasMessage } from '../i18n/utils';
import { useContext } from 'react';
import { AuthContext } from '../auth/AuthContext';
import { ROLE_PRECEDENCE } from '../auth/types';
import { IconAlertTriangle, IconInfo } from '../design-system/icons';
import './HelpNote.css';

/**
 * A one-line explanation of one control.
 *
 * `id` is the message key without the `help.` prefix, so
 * `<HelpNote id="tasks.kill" />` renders `help.tasks.kill`. Severity is
 * looked up in code (`HELP_SEVERITY`), never in the copy, so a
 * translation cannot quietly turn a danger note into a plain one.
 */
export function HelpNote({ id }: { id: string }) {
  const { enabled } = useHelpMode();
  const { t, locale } = useTranslate();
  if (!enabled) return null;
  const key = `help.${id}`;
  // A note whose key has not been written yet renders NOTHING rather
  // than the ⟦help.x⟧ marker. Screens are wired ahead of copy on
  // purpose -- the wiring is a code review, the words are a separate
  // one -- and a half-written locale must not put brackets and a raw
  // key in front of a hotel receptionist.
  if (!hasMessage(locale, key)) return null;
  const severity = severityOf(id);
  return (
    <p
      className={`nx-help-note nx-help-note--${severity}`}
      data-testid={`help-note-${id}`}
      data-severity={severity}
    >
      {severity === 'plain' ? (
        <IconInfo size={14} aria-hidden />
      ) : (
        <IconAlertTriangle size={14} aria-hidden />
      )}
      <span>{t(key)}</span>
    </p>
  );
}

/**
 * The paragraph at the top of a screen: what this screen is for, then
 * what the signed-in person's role can do on it.
 *
 * THE ROLE LINE IS THE POINT. The console renders no control a role
 * cannot use, so two people looking at the same screen see different
 * pages and neither can tell what the other sees. A front-desk operator
 * reading "you can watch here, and start a cleaning job; opening doors
 * is a supervisor's job" learns the shape of their own console, which
 * is the single most common confusion recorded from the site.
 *
 * The role line is optional per screen: `help.screen.<s>.role.<role>`
 * falls back to `...role.default`, and if neither exists only the intro
 * renders.
 */
export function HelpScreenNote({ screen }: { screen: string }) {
  const { enabled } = useHelpMode();
  const { t, locale } = useTranslate();
  // The auth CONTEXT, not `useHighestRole()`, which throws when no
  // provider is mounted. Everything else in this module renders
  // harmlessly without its provider, and a help paragraph must not be
  // the one thing in the app that can turn a missing provider into a
  // crash. No provider simply means no role line.
  const auth = useContext(AuthContext);
  const role = ROLE_PRECEDENCE.find((r) => auth?.roles?.includes(r)) ?? null;
  if (!enabled) return null;

  const introKey = `help.screen.${screen}.intro`;
  const bareRole = role ? role.replace(/^nexsoss:/, '') : '';
  const roleKey = bareRole ? `help.screen.${screen}.role.${bareRole}` : '';
  const defaultRoleKey = `help.screen.${screen}.role.default`;
  const resolvedRoleKey = roleKey && hasMessage(locale, roleKey)
    ? roleKey
    : hasMessage(locale, defaultRoleKey)
      ? defaultRoleKey
      : '';

  const hasIntro = hasMessage(locale, introKey);
  if (!hasIntro && !resolvedRoleKey) return null;

  return (
    <section
      className="nx-help-screen"
      aria-label="About this screen"
      data-testid={`help-screen-${screen}`}
    >
      <IconInfo size={16} aria-hidden />
      <div className="nx-help-screen__body">
        {hasIntro && <p className="nx-help-screen__intro">{t(introKey)}</p>}
        {resolvedRoleKey && (
          <p className="nx-help-screen__role" data-testid={`help-screen-role-${screen}`}>
            {t(resolvedRoleKey)}
          </p>
        )}
      </div>
    </section>
  );
}
